import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { setBoot } from "../../boot";
import { FilterOption, FilterState, getFilterMenu, getUpdatedFilterGroups } from "../../model/filters";
import { initialStateBTG, updateBronzeEligibilityData, updateBronzeGroupList } from "../bronzeGroupTracking";
import { AboBronzeEligibility, AboTileData } from "../../../services/BronzeGroupTracking/bronzeGroupTracking.types";
import {
  FilterBTGOption,
  FILTER_METHOD,
  findSearchResults,
  filterByTags,
  getUpdatedBGTMenuConfig,
  initialTrackingFilterGroup,
  BRONZE_IDS,
} from "../../model/bgtFilters";
import { BronzeGroupTracking } from "../../../common/interfaces/config";

const initialFilterGroups = initialTrackingFilterGroup.map((group) => {
  const { options } = group;
  return {
    ...group,
    options: options.map((option) => ({
      ...option,
      filterMethod: group.filterMethod as FILTER_METHOD,
    })),
  };
});

const intialFilterMenu = getFilterMenu(initialFilterGroups) as FilterBTGOption[];

interface BronzeGroupFilterState extends FilterState {
  filteredTrackingData: AboTileData[];
  trackingTilesData: AboTileData[];
  graduatedTilesData: AboTileData[];
  bronzeEligibilityData: AboBronzeEligibility[];
  isMemberPlusMarket: boolean;
}

const initialState: BronzeGroupFilterState = {
  showFilterMenu: false,
  reset: true,
  filterGroups: initialFilterGroups,
  filterMenu: intialFilterMenu,
  quickFilterMenu: [],
  searchKeyword: "",
  filterCount: 0,
  filterLastUpdatedAt: 0,
  filteredTrackingData: [] as AboTileData[],
  trackingTilesData: initialStateBTG.initialTrackingTilesData,
  graduatedTilesData: initialStateBTG.initialGraduatedTilesData,
  bronzeEligibilityData: [] as AboBronzeEligibility[],
  isMemberPlusMarket: false,
};

const { actions, reducer } = createSlice({
  name: "BGTTrackingFilter",
  initialState,
  reducers: {
    toggleFilterMenu: (state) => ({
      ...state,
      showFilterMenu: !state.showFilterMenu,
    }),

    // resets the filter to default options
    resetTrackingFilter: (state) => {
      const { filterMenu, filterGroups } = state;
      const _filterMenu = filterMenu.map((filter) => ({
        ...filter,
        checked: false,
      }));

      const { trackingTilesData, searchKeyword } = state;
      return {
        ...state,
        ...initialState,
        ...getUpdatedFilterGroups(filterGroups, _filterMenu),
        trackingTilesData,
        searchKeyword,
        showFilterMenu: state.showFilterMenu,
        filteredTrackingData: findSearchResults(searchKeyword, trackingTilesData),
        bronzeEligibilityData: state.bronzeEligibilityData,
        isMemberPlusMarket: state.isMemberPlusMarket,
      };
    },
    //reset search
    resetTrackingSearch: (state) => {
      const selectedOptions = state.filterMenu.filter((option) => option.checked) as FilterBTGOption[];
      return {
        ...state,
        searchKeyword: "",
        // check if there are selected filters and apply changes
        filteredTrackingData:
          selectedOptions.length > 0
            ? filterByTags(selectedOptions, state.trackingTilesData, state.bronzeEligibilityData)
            : state.trackingTilesData,
      };
    },

    // updates the filter options after clicking on apply filters
    updateFilterOptions: (state, action: PayloadAction<FilterBTGOption[]>) => {
      const { payload } = action;
      const { filterGroups } = state;
      const updatedFilters = getUpdatedFilterGroups(filterGroups, payload);
      return {
        ...state,
        ...updatedFilters,
      };
    },
    // search bronze group tracking
    updateSearchKeyword: (state, action: PayloadAction<string>) => {
      return { ...state, searchKeyword: action.payload, filterLastUpdatedAt: new Date().getTime() };
    },

    // search bronze group tracking list by abo number or name
    searchBronzeGroupTracking: (state, action: PayloadAction<{ searchKeyword: string }>) => {
      const { searchKeyword } = action.payload;
      return {
        ...state,
        filteredTrackingData: findSearchResults(
          searchKeyword,
          state.filterCount > 0 ? state.filteredTrackingData : state.trackingTilesData,
        ),
      };
    },

    filterTrackingBGT: (state) => {
      const selectedOptions = state.filterMenu.filter((option) => option.checked) as FilterBTGOption[];
      // if we have a search keyword let's use the filtered state otherwise use non-filtered data
      const searchResults = state.searchKeyword.length > 0 ? state.filteredTrackingData : state.trackingTilesData;

      return {
        ...state,
        filteredTrackingData:
          selectedOptions.length > 0
            ? filterByTags(selectedOptions, searchResults, state.bronzeEligibilityData)
            : searchResults,
      };
    },

    // updates single filter option, applicable for quickFilterMenu
    updateFilterOption: (state, action: PayloadAction<FilterOption>) => {
      const { payload } = action;
      const { filterGroups } = state;
      return {
        ...state,
        ...getUpdatedFilterGroups(filterGroups, [payload]),
      };
    },

    // Resets the existing filter and applies the given filter
    updateSelectedFilter: (state, action: PayloadAction<string>) => {
      const { payload } = action;
      const { filterMenu, filterGroups } = state;

      const _filterMenu = filterMenu.map((filter) => ({
        ...filter,
        checked: filter.id === payload,
      }));

      return {
        ...state,
        ...getUpdatedFilterGroups(filterGroups, _filterMenu),
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateBronzeGroupList, (state, action) => {
      const { initialTrackingTilesData, initialGraduatedTilesData } = action.payload;
      state.trackingTilesData = initialTrackingTilesData;
      state.graduatedTilesData = initialGraduatedTilesData;
    });

    builder.addCase(updateBronzeEligibilityData, (state, action) => {
      state.bronzeEligibilityData = action.payload;
    });

    builder.addCase(setBoot, (state, { payload }) => {
      const { tracking } = payload.configuration.bronzeGroupTracking as BronzeGroupTracking;
      const { filters = [], hideTabGroups = [] } = tracking;

      const { filterGroups, filterMenu } = state;
      const enableMenu = getUpdatedBGTMenuConfig(filters, filterMenu, "tracking");
      const updatedFilters = getUpdatedFilterGroups(
        filterGroups.filter(({ group }) => !hideTabGroups.includes(group)),
        enableMenu,
      );

      let isMemberPlusMarket = false;

      if (filters) {
        isMemberPlusMarket = filters.find((filter) => filter.id === BRONZE_IDS.TAGS_MEMBER_PLUS)?.enable || false;
      }

      return {
        ...state,
        ...updatedFilters,
        isMemberPlusMarket,
        filterLastUpdatedAt: new Date().getTime(),
      };
    });
  },
});

export const {
  toggleFilterMenu,
  resetTrackingFilter,
  resetTrackingSearch,
  updateFilterOption,
  updateFilterOptions,
  updateSearchKeyword,
  searchBronzeGroupTracking,
  filterTrackingBGT,
  updateSelectedFilter,
} = actions;

export default reducer;
